import React, { useEffect, useState } from 'react';
import { DailyTriviaGame } from '../Components/DailyTriviaGame';
import { DailySideBet } from '../Components/DailySideBet';
import MiniGamesLeaderboard from '../Components/MiniGamesLeaderboard';
import BreakawayDodger from '../Components/BreakawayDodger';
import GuessWhoGame from '../Components/GuessWhoGame';
import { fetchUserTokens } from '../Data/MiniGamesHelpers';
import { motion } from 'framer-motion';

const MiniGamesPage = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeGame, setActiveGame] = useState(null); // 'trivia' | 'sidebet' | 'leaderboard' | 'breakaway' | 'guesswho' | null

  useEffect(() => {
    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const userData = await fetchUserTokens();

      setCurrentUser(userData);
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(err.message || 'Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const refreshUserTokens = async () => {
    try {
      const userData = await fetchUserTokens();
      setCurrentUser(userData);
    } catch (err) {
      console.error('Error refreshing tokens:', err);
    }
  };

  if (loading) {
    return (
      <div className="minigames">
        <div className="minigames__status">Loading Mini Games...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="minigames">
        <div className="minigames__alert minigames__alert--error">{error}</div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="minigames">
        <div className="minigames__alert minigames__alert--warn">Please log in to access Mini Games</div>
      </div>
    );
  }

  return (
    <div className="minigames">
      <h1 className="minigames__headline">Mini Games</h1>

      <div className="minigames__token-board">
        <div className="minigames__token-item">
          <h5>Mini Game Tokens</h5>
          <h2>{currentUser.miniGameToken || 0}</h2>
          <small>Earn by completing trivia</small>
        </div>
        <div className="minigames__token-item">
          <h5>Bet Tokens</h5>
          <h2>{currentUser.betToken || 0}</h2>
          <small>Win from side bets</small>
        </div>
      </div>

      {!activeGame ? (
        <div className="minigames__grid">
          <motion.div className="minigames__span-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
            <div className="minigames__card">
                <h3>Daily Trivia</h3>
                <p className="card-text">
                  Answer 5 questions correctly (3/5 to pass) and earn a <strong>Mini Game Token</strong>.
                </p>
                <ul>
                  <li>Free to play once per day</li>
                  <li>Pass with 3+ correct answers</li>
                  <li>Earn 1 Mini Game Token when you pass</li>
                </ul>
                <button 
                  className="fb-btn fb-btn--primary"
                  onClick={() => setActiveGame('trivia')}
                >
                  Play Trivia
                </button>
            </div>
          </motion.div>

          <motion.div className="minigames__span-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.04 }}>
            <div className="minigames__card">
                <h3>Daily Side Bet</h3>
                <p className="card-text">
                  Predict the answer and win a <strong>Bet Token</strong>.
                </p>
                <ul>
                  <li>Costs 1 Mini Game Token to enter</li>
                  <li>Make your prediction</li>
                  <li>Correct answer earns 1 Bet Token</li>
                  <li>Free to check your results</li>
                </ul>
                <button 
                  className="fb-btn fb-btn--primary"
                  onClick={() => setActiveGame('sidebet')}
                >
                  {(currentUser.miniGameToken || 0) < 1 ? 'View Side Bet' : 'Place Side Bet'}
                </button>
                {(currentUser.miniGameToken || 0) < 1 && (
                  <small>
                    (You can still check results!)
                  </small>
                )}
            </div>
          </motion.div>

          <motion.div className="minigames__span-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.08 }}>
            <div className="minigames__card">
                <h3>Leaderboard</h3>
                <p className="card-text">
                  See who's dominating mini games!
                </p>
                <ul>
                  <li>Trivia & Breakaway stats</li>
                  <li>Top 3 this month</li>
                  <li>Top 3 all-time champions</li>
                </ul>
                <button 
                  className="fb-btn fb-btn--ghost"
                  onClick={() => setActiveGame('leaderboard')}
                >
                  View Leaderboard
                </button>
            </div>
          </motion.div>

          <motion.div className="minigames__span-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.12 }}>
            <div className="minigames__card">
                <h3>Breakaway Dodger</h3>
                <p className="card-text">
                  Dodge defenders and score!
                </p>
                <ul>
                  <li>FREE unlimited plays</li>
                  <li>First win each day: +1 token</li>
                  <li>Swipe or arrow keys</li>
                </ul>
                <button 
                  className="fb-btn fb-btn--ghost"
                  onClick={() => setActiveGame('breakaway')}
                >
                  Play Now
                </button>
            </div>
          </motion.div>

          <motion.div className="minigames__span-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28, delay: 0.16 }}>
            <div className="minigames__card">
                <h3>Guess Who?</h3>
                <p className="card-text">
                  Can you guess the athlete or celebrity from 5 fun hints?
                </p>
                <ul>
                  <li>5 hints per round</li>
                  <li>Includes NHL stars & actors</li>
                  <li>New challenge every play</li>
                </ul>
                <button 
                  className="fb-btn fb-btn--primary"
                  onClick={() => setActiveGame('guesswho')}
                >
                  Play Guess Who
                </button>
            </div>
          </motion.div>
        </div>
      ) : (
        <div>
            <button 
              className="fb-btn fb-btn--ghost minigames__back"
              onClick={() => setActiveGame(null)}
            >
              ← Back to Mini Games
            </button>

            {activeGame === 'trivia' && (
              <DailyTriviaGame 
                userId={currentUser.public_user_id}
                onComplete={() => {
                  refreshUserTokens();
                  setActiveGame(null);
                }}
              />
            )}

            {activeGame === 'sidebet' && (
              <DailySideBet 
                userId={currentUser.public_user_id}
                onComplete={() => {
                  refreshUserTokens();
                  setActiveGame(null);
                }}
              />
            )}

            {activeGame === 'leaderboard' && (
              <MiniGamesLeaderboard />
            )}

            {activeGame === 'breakaway' && (
              <BreakawayDodger 
                userId={currentUser.public_user_id}
                onComplete={() => {
                  refreshUserTokens();
                  setActiveGame(null);
                }}
              />
            )}

            {activeGame === 'guesswho' && (
              <GuessWhoGame />
            )}
        </div>
      )}
    </div>
  );
};

export default MiniGamesPage;
